/**
 * ColorSchemeModal.tsx
 *
 * Standard React color scheme selection modal using HTML/CSS
 * Based on Ego Somnium color scheme modal
 */
import React from 'react';
import { useTheme } from '../../services/ThemeContext';
import { PALETTES, ThemePalette } from '../../constants/styles';
import { ScrollableModal } from './Modal';

interface ColorSchemeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ColorSchemeModal: React.FC<ColorSchemeModalProps> = ({ isOpen, onClose }) => {
  const { themeName, setTheme, currentTheme: colors } = useTheme();

  // User-friendly display names for themes
  const themeDisplayNames: Record<string, string> = {
    LIGHT_CONSERVATIVE: 'Light Conservative',
    DARK_CONSERVATIVE: 'Dark Conservative',
    HIGH_CONTRAST_LIGHT: 'High Contrast Light',
    HIGH_CONTRAST_DARK: 'High Contrast Dark',
    VIVID_DARK: 'Vivid Dark',
  };

  // Theme pairs: light on left, corresponding dark on right
  const themePairs = [
    ['LIGHT_CONSERVATIVE', 'DARK_CONSERVATIVE'],
    ['HIGH_CONTRAST_LIGHT', 'HIGH_CONTRAST_DARK'],
  ];
  const extraThemes = ['VIVID_DARK']; // Themes without light/dark pair

  const themeOptionStyle = (isSelected: boolean) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 8,
    padding: 16,
    borderWidth: 2,
    borderColor: isSelected ? colors.primary : colors.border,
    borderStyle: 'solid',
    borderRadius: 12,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
  });

  const previewCircleStyle = (bgColor: string) => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: bgColor,
  });

  if (!isOpen) return null;

  return (
    <ScrollableModal isOpen={isOpen} onClose={onClose} title="Color Scheme">
      <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 16 }}>
        Choose your preferred color scheme
      </div>

      {/* Theme Pairs - Light on left, Dark on right */}
      {themePairs.map(([lightTheme, darkTheme]) => (
        <div key={lightTheme} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <button
            onClick={() => setTheme(lightTheme)}
            style={{
              ...themeOptionStyle(themeName === lightTheme),
              flex: 1,
              backgroundColor: PALETTES[lightTheme].surface,
            }}
          >
            <div style={previewCircleStyle(PALETTES[lightTheme].primary)} />
            <span style={{ color: PALETTES[lightTheme].textMain, fontSize: 12, textAlign: 'center' }}>
              {themeDisplayNames[lightTheme]}
            </span>
          </button>
          <button
            onClick={() => setTheme(darkTheme)}
            style={{
              ...themeOptionStyle(themeName === darkTheme),
              flex: 1,
              backgroundColor: PALETTES[darkTheme].surface,
            }}
          >
            <div style={previewCircleStyle(PALETTES[darkTheme].primary)} />
            <span style={{ color: PALETTES[darkTheme].textMain, fontSize: 12, textAlign: 'center' }}>
              {themeDisplayNames[darkTheme]}
            </span>
          </button>
        </div>
      ))}

      {/* Extra themes without pairs */}
      {extraThemes.map(name => (
        <button
          key={name}
          onClick={() => setTheme(name)}
          style={{
            ...themeOptionStyle(themeName === name),
            width: '100%',
            backgroundColor: PALETTES[name].surface,
            marginBottom: 12,
          }}
        >
          <div style={previewCircleStyle(PALETTES[name].primary)} />
          <span style={{ color: PALETTES[name].textMain, fontSize: 14 }}>
            {themeDisplayNames[name]}
          </span>
        </button>
      ))}
    </ScrollableModal>
  );
};

export { ColorSchemeModal };
